import { CherryPayApi } from "../../api/models";
import { isEmptyStr } from "../../util/StringUtil";
import DateUtil from "../../util/DateUtil";
import { NotificationFormProps, NotificationFormValues } from "./types";

export const handleNotificationSubmit = async (
  values: NotificationFormValues,
  props: NotificationFormProps,
  setFieldError: (field: string, message: string) => void
) => {
  let isInvalid = false;

  if (isEmptyStr(values.displayName)) {
    setFieldError("displayName", "Schedule Name is required");
    isInvalid = true;
  }

  const hasLocationOverrides = Object.values(
    values.locationMessageOverrides ?? {}
  ).some((v) => !isEmptyStr(v));

  // Non-super-admins must provide either a notification message or at least
  // one location message override. Super-admins may submit with neither
  // (the form surfaces a Silent Pass Update confirmation before reaching here).
  if (
    isEmptyStr(values.passUpdateMessage) &&
    !props.allowSilentEmptyPassUpdate &&
    !hasLocationOverrides
  ) {
    setFieldError(
      "passUpdateMessage",
      "Enter a notification message or override at least one location message"
    );
    isInvalid = true;
  }

  if (!values.sendDateTime) {
    setFieldError("sendDateTime", "Send Date & Time is required");
    isInvalid = true;
  }

  if (isEmptyStr(values.timeZoneId)) {
    setFieldError("timeZoneId", "Timezone is required");
    isInvalid = true;
  }

  if (hasLocationOverrides && !values.endDateTime) {
    setFieldError(
      "endDateTime",
      "End date is required when location messages are overridden"
    );
    isInvalid = true;
  }

  if (isInvalid) {
    return;
  }

  // Calculate Maximum Send DateTime
  let maximumLocalScheduleSendDateTime = null;
  const localDateTime = DateUtil.getLocalDateTimeInUTCFormatFromDateObject(
    values.sendDateTime
  );
  if (localDateTime && values.stopSendingOption === "specific") {
    const baseDate = new Date(localDateTime);

    let minutesToAdd = 0;

    if (values.stopSendingTimeUnit === "custom") {
      if (values.stopSendingCustomUnit === "minutes") {
        minutesToAdd = values.stopSendingCustomValue;
      } else {
        // hours
        minutesToAdd = Math.round(values.stopSendingCustomValue * 60);
      }
    } else {
      if (values.stopSendingTimeUnit === "minutes") {
        minutesToAdd = values.stopSendingTimeValue;
      } else {
        // hours
        minutesToAdd = values.stopSendingTimeValue * 60;
      }
    }

    if (!baseDate) {
      setFieldError("sendDateTime", "Send Date & Time is required");
      return;
    }

    const newDate = new Date(baseDate.getTime() + minutesToAdd * 60000);
    maximumLocalScheduleSendDateTime = newDate
      .toISOString()
      .replace(/\.[0-9]{3}Z$/, ".0000000Z");
  }

  const nonEmptyOverrides = Object.fromEntries(
    Object.entries(values.locationMessageOverrides ?? {}).filter(
      ([, message]) => !isEmptyStr(message)
    )
  );
  const startDateUtc = DateUtil.toUtcIsoString(values.sendDateTime);
  const endDateUtc = DateUtil.toUtcIsoString(values.endDateTime);
  const locationMessageOverride =
    Object.keys(nonEmptyOverrides).length > 0 && startDateUtc
      ? ({
          LocationMessageOverrides: nonEmptyOverrides,
          StartDateUtc: startDateUtc,
          EndDateUtc: endDateUtc as unknown as string,
        } as CherryPayApi.CherryPassNotificationLocationMessageOverride)
      : undefined;

  const notificationUpdate: CherryPayApi.CherryPassNotificationScheduleUpdate =
    {
      DisplayName: values.displayName,
      PassUpdateMessage: values.passUpdateMessage,
      TimeZoneId: values.timeZoneId!,
      LocalScheduleSendDateTime: localDateTime ?? "",
      MaximumLocalScheduleSendDateTime: maximumLocalScheduleSendDateTime,
      MemberFilterQuery: values.memberFilterQuery,
      MemberFilterSegment:
        values.filterMode === "segment" && values.memberFilterSegment != null
          ? values.memberFilterSegment
          : undefined,
      AutomaticallyScheduleForNewPassesGenerated: true,
      NotificationScheduleStatus: "Draft",
      LocationMessageOverride: locationMessageOverride,
    };

  try {
    // Determine if we're in edit mode
    const isEditing = props.notification !== null;
    const isScheduling = values.isScheduleRequested;
    let result;

    if (isEditing && props.notification?.Meta?.Id) {
      // Update existing notification
      result = await props.apiClient.updateNotification(
        notificationUpdate,
        props.businessId,
        props.notification.Meta.Id
      );

      // If scheduling was requested, call the schedule endpoint after successful update
      if (result.ok && isScheduling) {
        const scheduleResult = await props.apiClient.scheduleNotification(
          props.businessId,
          props.notification.Meta.Id
        );

        if (scheduleResult.ok) {
          if (props.onSuccess) {
            props.onSuccess(scheduleResult.data, isEditing);
          }
        } else {
          if (props.onFailure) {
            props.onFailure(
              `Notification updated but scheduling failed: ${scheduleResult.message}`,
              isEditing
            );
          }
        }
        return;
      }
    } else {
      // Create new notification
      result = await props.apiClient.createNotification(
        notificationUpdate,
        props.businessId
      );

      // If scheduling was requested, call the schedule endpoint after successful creation
      if (result.ok && isScheduling && result.data.Meta?.Id) {
        const scheduleResult = await props.apiClient.scheduleNotification(
          props.businessId,
          result.data.Meta.Id
        );

        if (scheduleResult.ok) {
          if (props.onSuccess) {
            props.onSuccess(scheduleResult.data, isEditing);
          }
        } else {
          if (props.onFailure) {
            props.onFailure(
              `Notification created but scheduling failed: ${scheduleResult.message}`,
              isEditing
            );
          }
        }
        return;
      }
    }

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess(result.data, isEditing);
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message, isEditing);
      }
    }
  } catch (error) {
    if (props.onFailure) {
      props.onFailure(
        "An unexpected error occurred",
        props.notification !== null
      );
    }
  }
};
